/**
 * Ghost Block Schema
 *
 * E012-T013: Ghost nodes for off-canvas relation targets.
 *
 * A ghost represents a database that exists in another document
 * but is referenced via a relation from a database on the current canvas.
 *
 * Features:
 * - Lightweight placeholder for cross-doc relations
 * - User can move/position on canvas
 * - Shows a navigation action only for targets the user can open
 * - Dashed border (visual indicator for "not on this canvas")
 * - Can receive relation edges
 */

import { I18n } from '@affine/i18n';
import type { GfxCompatibleProps } from '@blocksuite/std/gfx';
import { GfxCompatible } from '@blocksuite/std/gfx';
import {
  BlockModel,
  BlockSchemaExtension,
  defineBlockSchema,
} from '@blocksuite/store';

// =============================================================================
// Props Type
// =============================================================================

export type GhostTargetAccessState =
  | 'write'
  | 'read'
  | 'restricted'
  | 'missing'
  | 'trashed';

export type GhostBlockProps = {
  /** Source database ID on this canvas */
  sourceDatabaseId: string;
  /** Source element ID on this canvas */
  sourceElementId: string;
  /** Target database ID (in another doc) - or automationId for automation ghosts */
  targetDatabaseId: string;
  /** Target document ID */
  targetDocId: string;
  /** Cached target document title */
  targetDocTitle: string;
  /** Cached target database/automation title */
  targetDatabaseTitle: string;
  /** E018-S008-T008: Ghost type - 'database' (default) or 'automation' */
  ghostType: 'database' | 'automation';
  /** E018-SPK021: Whether the target database/doc is in trash */
  isTargetInTrash: boolean;
  /** Access state of the target endpoint from the current user's perspective */
  targetAccessState?: GhostTargetAccessState;
  /** For automation ghosts: direction of the local DB in the automation */
  automationDirection?: 'source' | 'target';
} & GfxCompatibleProps;

function tGhost(key: string, fallback: string): string {
  const translated = I18n.t(key);
  return translated && translated !== key ? translated : fallback;
}

// =============================================================================
// Model Class
// =============================================================================

export class GhostBlockModel extends GfxCompatible<GhostBlockProps>(
  BlockModel
) {
  /**
   * Get display title for the ghost
   */
  get displayTitle(): string {
    const fallbackKey =
      this.props.ghostType === 'automation'
        ? 'com.streamlain.streamlain.ghost.external-automation'
        : 'com.streamlain.streamlain.ghost.external-database';
    const fallback =
      this.props.ghostType === 'automation'
        ? 'External Automation'
        : 'External Database';
    return this.props.targetDatabaseTitle || tGhost(fallbackKey, fallback);
  }

  /**
   * Get display subtitle (doc title)
   */
  get displaySubtitle(): string {
    return (
      this.props.targetDocTitle ||
      tGhost(
        'com.streamlain.streamlain.ghost.another-document',
        'Another Document'
      )
    );
  }

  /**
   * Get the target document ID for navigation
   */
  get navigateToDocId(): string {
    return this.props.targetDocId;
  }

  /**
   * E018-SPK021: Whether the target database/doc is in trash
   */
  get isTargetInTrash(): boolean {
    return this.props.isTargetInTrash ?? false;
  }

  get targetAccessState(): GhostTargetAccessState | undefined {
    return this.props.targetAccessState;
  }

  get canOpenTargetDoc(): boolean {
    const docId = this.navigateToDocId;
    if (!docId || docId === 'unknown') return false;
    const accessState = this.targetAccessState;
    return accessState !== 'restricted' && accessState !== 'missing';
  }
}

// =============================================================================
// Default Values
// NOTE: These values MUST match constants.ts in frontend/core edge-infrastructure
// =============================================================================

const DEFAULT_GHOST_WIDTH = 200;
const DEFAULT_GHOST_HEIGHT = 100; // header(28) + subtitle(14) + link(14) + gaps(16) + padding(20) + buffer(8)

// =============================================================================
// Schema Definition
// =============================================================================

export const GhostBlockSchema = defineBlockSchema({
  flavour: 'affine:ghost',
  props: (): GhostBlockProps => ({
    // Cross-doc reference
    sourceDatabaseId: '',
    sourceElementId: '',
    targetDatabaseId: '',
    targetDocId: '',
    targetDocTitle: '',
    targetDatabaseTitle: '',
    // E018-S008-T008: Default to database ghost
    ghostType: 'database',
    // E018-SPK021: Trash status for styling
    isTargetInTrash: false,
    targetAccessState: undefined,

    // GfxCompatible props
    xywh: `[0,0,${DEFAULT_GHOST_WIDTH},${DEFAULT_GHOST_HEIGHT}]`,
    index: 'a0',
    lockedBySelf: false,
  }),
  metadata: {
    role: 'content',
    version: 1,
    parent: ['@root'], // Canvas only - ghosts don't belong in notes
    children: [], // No children
  },
  toModel: () => new GhostBlockModel(),
});

// =============================================================================
// Schema Extension
// =============================================================================

export const GhostBlockSchemaExtension = BlockSchemaExtension(GhostBlockSchema);
